import React, { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { canUpgradeToPaid, isPaidPlan } from "../lib/billing";

import { AppVersionText } from "../components/AppVersionText";
import { ReLoginMethodNotice } from "../components/ReLoginMethodNotice";
import { useAccountState } from "../hooks/useAccountState";
import { useHomeData } from "../hooks/useHomeData";
import { useLevelUpCelebration } from "../hooks/useLevelUpCelebration";
import {
  api,
  type AccountState,
  type ChatModeItem,
  type FeatureKey,
  type InviteApplyErrorCode,
  type PrivilegeApplyErrorCode,
} from "../lib/api";
import { getChatModeDisplayName } from "../lib/chatModeDisplay";
import { getCharacterShortName } from "../lib/characterName";
import { AppError, toUserMessage } from "../lib/errors";
import { canUseAnyFeature, FEATURE_LOCK_MESSAGE, hasActiveEntitlement } from "../lib/featureAccess";
import { writePendingJobNotification } from "../lib/jobNotifications";
import {
  GOOGLE_SCOPE_BUNDLES,
  getGoogleIntegrationAggregateStatusLabel,
  resolveGoogleIntegrationAggregateState,
} from "../lib/googleScopes";
import {
  getLineLinkStatusLabel,
  resolveLineLinkSnapshotFromAccountState,
} from "../lib/lineLink";
import { logoutToSplash } from "../lib/logout";
import { storage } from "../lib/storage";

const AI_EXECUTIVE_OFFICE_GREETING =
  "代表、お疲れさまです！\n相談したいことも、頼みたいことも、ここから一緒に進めましょう。";
const LEGACY_HOME_GREETING =
  "代表、お疲れさまです！少し一息入れませんか。こちらはいつでも動けるように準備しています！！";
const TALK_FEATURES: FeatureKey[] = ["chat", "group_talk"];
const REQUEST_FEATURES: FeatureKey[] = [
  "news",
  "report",
  "company_research",
  "receipt_ocr",
  "memo_ocr",
];

const FEATURE_USAGE_ROWS: Array<{ feature: FeatureKey; limitKey: string; label: string }> = [
  { feature: "chat", limitKey: "monthly_messages", label: "チャット" },
  { feature: "group_talk", limitKey: "monthly_turns", label: "壁打ちターン" },
  { feature: "news", limitKey: "manual_runs_monthly", label: "ニュース" },
  { feature: "report", limitKey: "manual_runs_monthly", label: "レポート" },
  { feature: "company_research", limitKey: "monthly_companies", label: "企業調査" },
  { feature: "company_research", limitKey: "deep_dive_monthly", label: "企業調査 深掘り" },
  { feature: "receipt_ocr", limitKey: "monthly_receipts", label: "領収書OCR" },
  { feature: "memo_ocr", limitKey: "monthly_images", label: "社長メモOCR" },
];

const PLAN_LABELS: Record<string, string> = {
  none: "未設定",
  free: "Free",
  invite_trial: "特典コード適用中",
  paid: "Standard",
  internal: "Internal",
};

const inviteErrorMessages: Record<InviteApplyErrorCode, string> = {
  invalid_invite_code: "招待コードが見つかりません。入力内容を確認してください。",
  invite_code_not_started: "この招待コードはまだ利用開始前です。",
  invite_code_expired: "この招待コードの有効期限が切れています。",
  invite_code_usage_limit_exceeded: "この招待コードは利用上限に達しています。",
  invite_code_inactive: "この招待コードは現在利用できません。",
};

function isInviteApplyErrorCode(value: unknown): value is InviteApplyErrorCode {
  return (
    value === "invalid_invite_code" ||
    value === "invite_code_not_started" ||
    value === "invite_code_expired" ||
    value === "invite_code_usage_limit_exceeded" ||
    value === "invite_code_inactive"
  );
}

function extractInviteApplyErrorCode(error: unknown): InviteApplyErrorCode | null {
  if (!(error instanceof AppError) || !error.meta.responseText) return null;

  try {
    const parsed = JSON.parse(error.meta.responseText) as {
      errorCode?: unknown;
      error_code?: unknown;
      data?: { errorCode?: unknown; error_code?: unknown };
    };
    const errorCode = parsed.errorCode ?? parsed.error_code ?? parsed.data?.errorCode ?? parsed.data?.error_code;
    return isInviteApplyErrorCode(errorCode) ? errorCode : null;
  } catch {
    return null;
  }
}

const privilegeErrorMessages: Record<PrivilegeApplyErrorCode, string> = {
  invalid_privilege_code: "特典コードが見つかりません。入力内容を確認してください。",
};

function isPrivilegeApplyErrorCode(value: unknown): value is PrivilegeApplyErrorCode {
  return value === "invalid_privilege_code";
}

function extractPrivilegeApplyErrorCode(error: unknown): PrivilegeApplyErrorCode | null {
  if (!(error instanceof AppError) || !error.meta.responseText) return null;

  try {
    const parsed = JSON.parse(error.meta.responseText) as {
      errorCode?: unknown;
      error_code?: unknown;
      data?: { errorCode?: unknown; error_code?: unknown };
    };
    const errorCode = parsed.errorCode ?? parsed.error_code ?? parsed.data?.errorCode ?? parsed.data?.error_code;
    return isPrivilegeApplyErrorCode(errorCode) ? errorCode : null;
  } catch {
    return null;
  }
}

function findUnacknowledgedUnlockedMode(
  characterId: string | null,
  modes: ChatModeItem[]
): ChatModeItem | null {
  if (!characterId) return null;
  const acknowledged = storage.getAcknowledgedUnlockedModes(characterId);
  return modes.find((m) => m.mode !== 0 && m.unlocked && !acknowledged.includes(m.mode)) ?? null;
}

function resolveHomeGreeting(messageToUser: string) {
  const trimmed = messageToUser.trim();
  if (!trimmed || trimmed === LEGACY_HOME_GREETING) {
    return AI_EXECUTIVE_OFFICE_GREETING;
  }
  return messageToUser;
}

type MenuButtonProps = {
  title: string;
  description?: string;
  status?: string;
  disabled?: boolean;
  destructive?: boolean;
  onClick?: () => void;
};

function MenuButton({ title, description, status, disabled, destructive, onClick }: MenuButtonProps) {
  const resolvedTitle = title === "プロフィール設定" ? "プロファイル設定" : title;
  const resolvedDescription =
    title === "プロフィール設定" ? "あなたの情報を設定できます。" : description;

  return (
    <button
      type="button"
      aria-label={resolvedTitle}
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-start rounded-[1.1rem] px-4 py-3 text-left transition ${
        disabled ? "cursor-not-allowed opacity-55" : "hover:bg-[#FDF8F5]"
      }`}
    >
      <span className="min-w-0">
        <span className="flex items-center gap-2">
          <span className={`text-sm font-bold ${destructive ? "text-[#A1493E]" : "text-[#5D4D46]"}`}>{resolvedTitle}</span>
          {status ? (
            <span
              className={`rounded-full px-2 py-0.5 text-[10px] font-black ${
                disabled
                  ? "border border-[#E7DDD6] bg-[#F7F2EE] text-[#A1887D]"
                  : "border border-[#E6DACD] bg-[#FFF8F1] text-[#A46D4C]"
              }`}
            >
              {status}
            </span>
          ) : null}
        </span>
        {resolvedDescription ? (
          <span className="mt-1 block text-xs font-medium leading-5 text-[#7B6861]">{resolvedDescription}</span>
        ) : null}
      </span>
    </button>
  );
}

type MenuSectionId = "usage" | "integrations" | "policies" | "other";

type MenuRootButtonProps = {
  title: string;
  hasChildren?: boolean;
  onClick: () => void;
};

function MenuRootButton({ title, hasChildren = true, onClick }: MenuRootButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center justify-between rounded-[1.1rem] px-4 py-3 text-left transition hover:bg-[#FDF8F5]"
    >
      <span className="text-sm font-bold text-[#5D4D46]">{title}</span>
      {hasChildren ? (
        <span className="text-sm font-black text-[#A67C52]" aria-hidden="true">
          →
        </span>
      ) : null}
    </button>
  );
}

type HomeActionCardProps = {
  to: string;
  title: string;
  description: string;
  locked: boolean;
  variant: "primary" | "secondary";
  onLockedClick: () => void;
};

function HomeActionCard({ to, title, description, locked, variant, onLockedClick }: HomeActionCardProps) {
  const baseClass =
    "relative rounded-[1.8rem] px-4 py-5 text-center shadow-sm transition-all active:scale-[0.98]";
  const primaryClass = "bg-[#5D4D46] text-[#FDF8F5] shadow-xl hover:bg-[#4A3E39]";
  const secondaryClass = "border border-[#D9C5B9] bg-white/85 text-[#5D4D46] hover:bg-white";
  const lockedClass = "border border-[#E0D5CE] bg-[#F4EEE9] text-[#8A756C] opacity-85";
  const className = `${baseClass} ${locked ? lockedClass : variant === "primary" ? primaryClass : secondaryClass}`;
  const descriptionClass = variant === "primary" && !locked ? "text-[#F6E8DE]" : "text-[#8B7065]";

  const content = (
    <>
      {locked ? (
        <span className="absolute right-3 top-3 rounded-full bg-white px-2 py-1 text-[10px] font-black text-[#A1887D]">
          ロック中
        </span>
      ) : null}
      <span className="block text-lg font-black tracking-wide">{title}</span>
      <span className={`mt-1 block text-xs font-medium ${descriptionClass}`}>
        {locked ? FEATURE_LOCK_MESSAGE : description}
      </span>
    </>
  );

  if (locked) {
    return (
      <button type="button" className={className} aria-disabled="true" onClick={onLockedClick}>
        {content}
      </button>
    );
  }

  return (
    <Link to={to} className={className}>
      {content}
    </Link>
  );
}

type UsageRow = {
  label: string;
  limit: number | null;
  used: number;
  remaining: number | null;
  isUnlimited: boolean;
};

function buildUsageRows(accountState: AccountState | null): UsageRow[] {
  const featureUsage = accountState?.featureUsage ?? {};

  return FEATURE_USAGE_ROWS.flatMap(({ feature, limitKey, label }): UsageRow[] => {
    const counter = featureUsage[feature]?.[limitKey];
    if (!counter) return [];

    if (counter.isUnlimited) {
      return [{ label, limit: null, used: Math.max(0, Math.floor(counter.used)), remaining: null, isUnlimited: true }];
    }

    return [
      {
        label,
        limit: Math.max(0, Math.floor(counter.limit ?? 0)),
        used: Math.max(0, Math.floor(counter.used)),
        remaining: Math.max(0, Math.floor(counter.remaining ?? 0)),
        isUnlimited: false,
      },
    ];
  });
}

function usageBarWidth(row: UsageRow): string {
  if (row.isUnlimited) return "100%";
  if (row.limit === null || row.limit <= 0) return "100%";
  const percent = Math.min(100, Math.max(0, (row.used / row.limit) * 100));
  return `${percent}%`;
}

function formatEntitlementExpiresAt(value: string | null): string | null {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).format(date);
}

function usageStatus(row: UsageRow): "ok" | "low" | "empty" {
  if (row.isUnlimited) return "ok";
  if (row.limit === null || row.limit <= 0 || (row.remaining ?? 0) <= 0) return "empty";
  if ((row.remaining ?? 0) <= Math.max(1, Math.ceil(row.limit * 0.2))) return "low";
  return "ok";
}

function usageStatusLabel(row: UsageRow): string {
  if (row.isUnlimited) return "無制限";
  if (row.limit === null || row.limit <= 0) return "利用不可";
  if ((row.remaining ?? 0) <= 0) return "上限";
  return `残り ${row.remaining} / ${row.limit}`;
}

function UsageSummary({
  accountState,
  rows,
  titleId,
}: {
  accountState: AccountState;
  rows: UsageRow[];
  titleId?: string;
}) {
  const planLabel = PLAN_LABELS[accountState.plan] ?? accountState.plan;
  // 自動更新(サブスクリプション)の期限は「請求期間の終了日」であり失効日ではないため表示しない。
  // 特典コード・協力者コードなど期限付き付与のみ、いつまで使えるかを案内する。
  const expiresAtLabel = accountState.entitlementAutoRenews
    ? null
    : formatEntitlementExpiresAt(accountState.entitlementExpiresAt);

  return (
    <section className="mb-8 w-full rounded-[1.8rem] border border-[#EADBC8] bg-white/75 px-5 py-5 shadow-[0_12px_30px_rgba(124,90,78,0.08)]">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-black uppercase tracking-[0.14em] text-[#A67C52]">USAGE</p>
          <h2 id={titleId} className="mt-1 text-base font-black text-[#5D4D46]">
            今月の利用状況
          </h2>
        </div>
        <div className="shrink-0 text-right">
          <span className="inline-block rounded-full border border-[#E6DACD] bg-[#FFF8F1] px-3 py-1 text-[11px] font-black text-[#A46D4C]">
            {planLabel}
          </span>
          {expiresAtLabel ? (
            <p className="mt-1 text-[11px] font-bold text-[#A67C52]">{expiresAtLabel} まで</p>
          ) : null}
        </div>
      </div>

      {rows.length > 0 ? (
        <ul className="mt-4 space-y-3">
          {rows.map((row) => {
            const status = usageStatus(row);
            const barClass =
              status === "empty" ? "bg-[#B65A4D]" : status === "low" ? "bg-[#D39A45]" : "bg-[#6E9B73]";

            return (
              <li key={`${row.label}-${row.limit}`} className="border-t border-[#F0E4DA] pt-3 first:border-t-0 first:pt-0">
                <div className="flex items-center justify-between gap-3">
                  <span className="min-w-0 text-sm font-black text-[#5D4D46]">{row.label}</span>
                  <span className="shrink-0 text-xs font-black text-[#A67C52]">{usageStatusLabel(row)}</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#F1E8E0]">
                  <div className={`h-full rounded-full ${barClass}`} style={{ width: usageBarWidth(row) }} />
                </div>
                <p className="mt-1 text-[11px] font-medium text-[#8B7065]">使用済み {row.used}</p>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-4 rounded-xl bg-[#FFF8F1] px-3 py-3 text-xs font-bold leading-5 text-[#8B7065]">
          利用状況はまだありません。
        </p>
      )}
    </section>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const {
    loading,
    error,
    vm,
    currentCharacterId,
    todayJobLogs,
    yesterdayJobLogs,
    messageToUser,
    lineLinkSnapshot,
    googleIntegrationViews,
    refresh,
    refreshAgentJobs,
    hasActiveAgentJobs,
  } = useHomeData();
  const {
    accountState,
    loading: accountStateLoading,
    error: accountStateError,
    reload: reloadAccountState,
  } = useAccountState();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeMenuSection, setActiveMenuSection] = useState<MenuSectionId | null>(null);
  const [portraitSrc, setPortraitSrc] = useState("/img/no-image.png");
  const [showYesterday, setShowYesterday] = useState(false);
  const [expandedJobLogs, setExpandedJobLogs] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [inviteApplyLoading, setInviteApplyLoading] = useState(false);
  const [inviteApplyError, setInviteApplyError] = useState<string | null>(null);
  const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);

  const [isPrivilegeModalOpen, setIsPrivilegeModalOpen] = useState(false);
  const [privilegeCode, setPrivilegeCode] = useState("");
  const [privilegeApplyLoading, setPrivilegeApplyLoading] = useState(false);
  const [privilegeApplyError, setPrivilegeApplyError] = useState<string | null>(null);
  const [privilegeApplySuccess, setPrivilegeApplySuccess] = useState(false);

  const characterLastName = getCharacterShortName(vm?.characterName);
  const pageSize = 4;
  const displayedJobLogs = showYesterday ? yesterdayJobLogs : todayJobLogs;
  const hasOlderLogs = yesterdayJobLogs.length > 0;
  const visibleJobLogs = expandedJobLogs ? displayedJobLogs : displayedJobLogs.slice(0, pageSize);
  const hiddenCount = displayedJobLogs.length - pageSize;
  const shouldShowInviteEntry =
    accountState?.authStatus === "authenticated" && !hasActiveEntitlement(accountState);
  const shouldShowUpgrade = accountState?.authStatus === "authenticated" && (accountState.billingEnabled ?? false) && canUpgradeToPaid(accountState);
  const shouldShowBillingPortal = accountState?.authStatus === "authenticated" && (accountState.billingEnabled ?? false) && isPaidPlan(accountState);
  const canOpenTalk = accountState ? canUseAnyFeature(accountState, TALK_FEATURES) : false;
  const canOpenRequest = accountState ? canUseAnyFeature(accountState, REQUEST_FEATURES) : false;
  const isTalkLocked = accountStateLoading || !accountState || !canOpenTalk;
  const isRequestLocked = accountStateLoading || !accountState || !canOpenRequest;
  const accountUserId = storage.getAuth().userId ?? null;
  const usageRows = useMemo(() => buildUsageRows(accountState), [accountState]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const handleFocus = () => {
      void refreshAgentJobs();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refreshAgentJobs();
      }
    };

    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshAgentJobs]);

  useEffect(() => {
    if (!hasActiveAgentJobs) return;

    const timer = window.setInterval(() => {
      void refreshAgentJobs();
    }, 15000);

    return () => {
      window.clearInterval(timer);
    };
  }, [hasActiveAgentJobs, refreshAgentJobs]);

  useEffect(() => {
    setExpandedJobLogs(false);
  }, [showYesterday]);

  useEffect(() => {
    setPortraitSrc(vm?.imageUrl || "/img/no-image.png");
  }, [vm?.imageUrl]);

  useEffect(() => {
    if (loading || !vm || !currentCharacterId) return;

    (async () => {
      try {
        const response = await api.getChatModes({ characterId: currentCharacterId });
        const notification = findUnacknowledgedUnlockedMode(currentCharacterId, response.modes);
        if (notification) {
          writePendingJobNotification({
            kind: "mode_unlock",
            characterId: currentCharacterId,
            mode: notification.mode,
            modeName: getChatModeDisplayName(notification),
          });
        }
      } catch {
        // ホーム表示は継続する
      }
    })();
  }, [loading, vm, currentCharacterId]);

  const closeMenu = () => {
    setActiveMenuSection(null);
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    if (isMenuOpen) {
      closeMenu();
      return;
    }
    setActiveMenuSection(null);
    setIsMenuOpen(true);
  };

  const openInviteModal = useCallback(() => {
    setInviteCode("");
    setInviteApplyError(null);
    setIsInviteModalOpen(true);
  }, []);

  const closeInviteModal = useCallback(() => {
    if (inviteApplyLoading) return;
    setIsInviteModalOpen(false);
    setInviteCode("");
    setInviteApplyError(null);
  }, [inviteApplyLoading]);

  const openInviteModalFromMenu = useCallback(() => {
    closeMenu();
    openInviteModal();
  }, [openInviteModal]);

  const openPrivilegeModal = useCallback(() => {
    setPrivilegeCode("");
    setPrivilegeApplyError(null);
    setPrivilegeApplySuccess(false);
    setIsPrivilegeModalOpen(true);
  }, []);

  const closePrivilegeModal = useCallback(() => {
    if (privilegeApplyLoading) return;
    setIsPrivilegeModalOpen(false);
    setPrivilegeCode("");
    setPrivilegeApplyError(null);
    setPrivilegeApplySuccess(false);
  }, [privilegeApplyLoading]);

  const openPrivilegeModalFromMenu = useCallback(() => {
    closeMenu();
    openPrivilegeModal();
  }, [openPrivilegeModal]);

  const openUsageModalFromMenu = useCallback(() => {
    closeMenu();
    setIsUsageModalOpen(true);
  }, []);

  const closeUsageModal = useCallback(() => {
    setIsUsageModalOpen(false);
  }, []);

  const handlePrivilegeApply = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (privilegeApplyLoading) return;
      if (accountState?.authStatus !== "authenticated") {
        setPrivilegeApplyError("ログイン後に特典コードを適用してください。");
        return;
      }

      const normalizedCode = privilegeCode.trim();
      if (!normalizedCode) {
        setPrivilegeApplyError("特典コードを入力してください。");
        return;
      }

      setPrivilegeApplyLoading(true);
      setPrivilegeApplyError(null);

      try {
        const result = await api.applyPrivilegeCode(normalizedCode);
        await reloadAccountState();
        setPrivilegeCode("");
        setPrivilegeApplyError(null);

        if (result.alreadyApplied) {
          setPrivilegeApplyError("この特典コードはすでに適用済みです。");
        } else {
          setPrivilegeApplySuccess(true);
        }
      } catch (errorValue) {
        const errorCode = extractPrivilegeApplyErrorCode(errorValue);
        setPrivilegeApplyError(errorCode ? privilegeErrorMessages[errorCode] : toUserMessage(errorValue));
      } finally {
        setPrivilegeApplyLoading(false);
      }
    },
    [accountState?.authStatus, privilegeApplyLoading, privilegeCode, reloadAccountState]
  );

  const openCharacterSettings = () => {
    closeMenu();
    navigate("/character-switch");
  };

  const openProfileSettings = () => {
    closeMenu();
    navigate("/profile", { state: { returnTo: "/home" } });
  };

  const openLoginMethods = () => {
    closeMenu();
    navigate("/profile/login-methods");
  };

  const openLineLink = () => {
    closeMenu();
    navigate("/line-link", { state: { returnTo: "/home" } });
  };

  const openGoogleLink = () => {
    closeMenu();
    navigate(`/google-link?returnTo=${encodeURIComponent("/home")}&mode=selector`, {
      state: { returnTo: "/home", mode: "selector" },
    });
  };

  const openTermsPage = () => {
    closeMenu();
    navigate("/terms");
  };

  const openPrivacyPolicy = () => {
    closeMenu();
    navigate("/privacy");
  };

  const openCommercialTransactions = () => {
    closeMenu();
    navigate("/commercial-transactions");
  };

  const openPlansPage = () => {
    closeMenu();
    navigate("/plans", { state: { returnTo: "/home" } });
  };

  const handleInviteApply = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (inviteApplyLoading) return;
      if (accountState?.authStatus !== "authenticated") {
        setInviteApplyError("ログイン後に招待コードを適用してください。");
        return;
      }

      const normalizedInviteCode = inviteCode.trim();
      if (!normalizedInviteCode) {
        setInviteApplyError("招待コードを入力してください。");
        return;
      }

      setInviteApplyLoading(true);
      setInviteApplyError(null);

      try {
        const result = await api.applyInviteCode(normalizedInviteCode);
        if (result.errorCode) {
          setInviteApplyError(inviteErrorMessages[result.errorCode]);
          return;
        }

        await reloadAccountState();
        setInviteCode("");
        setInviteApplyError(null);
        setIsInviteModalOpen(false);
      } catch (errorValue) {
        const errorCode = extractInviteApplyErrorCode(errorValue);
        setInviteApplyError(errorCode ? inviteErrorMessages[errorCode] : toUserMessage(errorValue));
      } finally {
        setInviteApplyLoading(false);
      }
    },
    [accountState?.authStatus, inviteApplyLoading, inviteCode, reloadAccountState]
  );

  const handleLogout = () => {
    logoutToSplash();
  };

  const accountLineLinkSnapshot = useMemo(
    () => resolveLineLinkSnapshotFromAccountState(accountState, lineLinkSnapshot),
    [accountState, lineLinkSnapshot]
  );
  const lineLinkStatusLabel = getLineLinkStatusLabel(accountLineLinkSnapshot);
  const bundleGoogleConnected =
    googleIntegrationViews[GOOGLE_SCOPE_BUNDLES.bundle].state === "connected";
  const googleAggregateStatus = bundleGoogleConnected
    ? resolveGoogleIntegrationAggregateState({
        [GOOGLE_SCOPE_BUNDLES.bundle]: true,
        [GOOGLE_SCOPE_BUNDLES.calendarToday]:
          googleIntegrationViews[GOOGLE_SCOPE_BUNDLES.calendarToday].state === "connected",
      })
    : resolveGoogleIntegrationAggregateState({
        [GOOGLE_SCOPE_BUNDLES.bundle]:
          googleIntegrationViews[GOOGLE_SCOPE_BUNDLES.receipt].state === "connected",
        [GOOGLE_SCOPE_BUNDLES.calendarToday]:
          googleIntegrationViews[GOOGLE_SCOPE_BUNDLES.calendarToday].state === "connected",
      });
  const googleLinkStatusLabel = getGoogleIntegrationAggregateStatusLabel(googleAggregateStatus);

  useLevelUpCelebration(vm?.rankName, currentCharacterId, "home");

  return (
    <div className="min-h-screen bg-[#FDF8F5] text-[#4A3E39] antialiased">
      <div className="mx-auto min-h-screen w-full max-w-md px-6 pb-6 pt-10 sm:px-8">
        <header className="flex items-center justify-between pb-4">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-[#7C5A4E]">WizMate</h1>
            <p className="mt-0.5 text-[11px] font-bold tracking-[0.1em] text-[#A67C52]">AI EXECUTIVE OFFICE</p>
          </div>

          <div className="relative">
            <button
              type="button"
              onClick={toggleMenu}
              aria-label="メニュー"
              aria-expanded={isMenuOpen}
              className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[#EADBC8] bg-white/70 shadow-sm"
            >
              <span className="sr-only">メニュー</span>
              <span className="flex flex-col items-center justify-center gap-1">
                <span className="block h-0.5 w-5 rounded bg-[#7C5A4E]" />
                <span className="block h-0.5 w-5 rounded bg-[#7C5A4E]" />
                <span className="block h-0.5 w-5 rounded bg-[#7C5A4E]" />
              </span>
            </button>

            {isMenuOpen ? (
              <>
                <button
                  type="button"
                  aria-label="メニューを閉じる"
                  className="fixed inset-0 z-10 cursor-default bg-transparent"
                  onClick={closeMenu}
                />

                <div className="absolute right-0 top-14 z-20 max-h-[min(78vh,34rem)] w-72 overflow-y-auto rounded-[1.6rem] border border-[#EADBC8] bg-white/95 p-2 shadow-[0_18px_40px_rgba(124,90,78,0.18)] backdrop-blur-sm">
                  {activeMenuSection ? (
                    <>
                      <div className="mb-1 px-2 pt-1">
                        <button
                          type="button"
                          onClick={() => setActiveMenuSection(null)}
                          className="rounded-full px-2 py-1 text-xs font-black text-[#A67C52] transition hover:bg-[#FDF8F5] hover:text-[#7C5A4E]"
                        >
                          ←戻る
                        </button>
                        <p className="mt-1 px-2 text-sm font-black text-[#5D4D46]">
                          {activeMenuSection === "usage"
                            ? "利用状況"
                            : activeMenuSection === "integrations"
                            ? "外部連携"
                            : activeMenuSection === "policies"
                            ? "規約・ポリシー"
                            : "その他"}
                        </p>
                      </div>

                      {activeMenuSection === "usage" ? (
                        <>
                          {accountState?.authStatus === "authenticated" ? (
                            <MenuButton
                              title="利用状況"
                              description="利用回数を確認します。プラン変更もこちら。"
                              onClick={openUsageModalFromMenu}
                            />
                          ) : null}
                          {shouldShowUpgrade ? (
                            <MenuButton
                              title="プランを管理"
                              description="料金とプラン内容を確認してから変更できます。"
                              onClick={openPlansPage}
                            />
                          ) : null}
                          {shouldShowBillingPortal ? (
                            <MenuButton
                              title="プランを管理"
                              description="料金とプラン内容を確認してから変更できます。"
                              onClick={openPlansPage}
                            />
                          ) : null}
                          {accountState?.authStatus === "authenticated" ? (
                            <MenuButton
                              title="特典コードを入力"
                              description="追加特典の受け取りはこちら。"
                              onClick={openPrivilegeModalFromMenu}
                            />
                          ) : null}
                          {shouldShowInviteEntry ? (
                            <MenuButton
                              title="招待コードを入力"
                              description="招待コードを入力してWizMateを使えるようにします。"
                              onClick={openInviteModalFromMenu}
                            />
                          ) : null}
                        </>
                      ) : null}

                      {activeMenuSection === "integrations" ? (
                        <>
                          <MenuButton
                            title="Google連携"
                            description="ファイル保存・カレンダー確認に使います。"
                            status={googleLinkStatusLabel}
                            onClick={openGoogleLink}
                          />
                          <MenuButton
                            title="LINE連携"
                            description="LINEでニュースを受け取ることができます。"
                            status={lineLinkStatusLabel}
                            onClick={openLineLink}
                          />
                        </>
                      ) : null}

                      {activeMenuSection === "policies" ? (
                        <>
                          <MenuButton title="利用規約" onClick={openTermsPage} />
                          <MenuButton title="プライバシーポリシー" onClick={openPrivacyPolicy} />
                          <MenuButton title="特定商取引法に基づく表記" onClick={openCommercialTransactions} />
                        </>
                      ) : null}

                      {activeMenuSection === "other" ? (
                        <>
                          {accountUserId ? (
                            <div className="mx-3 mb-2 rounded-[1rem] border border-[#EADBC8] bg-[#FDF8F5] px-3 py-2">
                              <p className="text-[10px] font-black tracking-[0.08em] text-[#A67C52]">アカウント</p>
                              <p className="mt-1 text-[11px] font-medium leading-5 text-[#7B6861]">
                                お問い合わせ時にお伝えください
                              </p>
                              <p className="mt-1 break-all font-mono text-[11px] font-bold text-[#5D4D46]">
                                ユーザーID: <span className="select-all">{accountUserId}</span>
                              </p>
                            </div>
                          ) : null}
                          <MenuButton
                            title="ログアウト"
                            description="ログイン方法を選び直します。"
                            onClick={handleLogout}
                          />
                        </>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <p className="px-4 pb-1 pt-2 text-xs font-black tracking-[0.08em] text-[#A67C52]">WizMate設定</p>
                      <MenuRootButton title="利用状況" onClick={() => setActiveMenuSection("usage")} />
                      <MenuRootButton title="プロファイル設定" hasChildren={false} onClick={openProfileSettings} />
                      <MenuRootButton title="ログイン方法" hasChildren={false} onClick={openLoginMethods} />
                      <MenuRootButton title="キャラクター選択" hasChildren={false} onClick={openCharacterSettings} />
                      <MenuRootButton title="外部連携" onClick={() => setActiveMenuSection("integrations")} />
                      <MenuRootButton title="規約・ポリシー" onClick={() => setActiveMenuSection("policies")} />
                      <MenuRootButton title="その他" onClick={() => setActiveMenuSection("other")} />
                    </>
                  )}

                  <AppVersionText className="px-4 pb-1 pt-2 text-center text-[10px] font-medium text-[#C4AFA8]" />
                </div>
              </>
            ) : null}
          </div>
        </header>

        <main className="flex flex-1 flex-col items-center">
          {loading ? <p className="mt-3 text-center text-xs text-[#8E6E60]">読み込み中です...</p> : null}
          {!loading && error ? <p className="mt-3 text-center text-xs text-[#AF3B2E]">{error}</p> : null}
          {accountStateLoading ? (
            <p className="mt-1 text-center text-xs text-[#8E6E60]">利用状態を確認しています...</p>
          ) : null}
          {!accountStateLoading && accountStateError ? (
            <p className="mt-1 text-center text-xs text-[#AF3B2E]">{accountStateError}</p>
          ) : null}

          {!accountStateLoading && accountState?.entitlementStatus === "expired" ? (
            <div className="mt-4 w-full rounded-[1.6rem] border border-[#E6DACD] bg-[#FFF8F1] px-5 py-5 text-[#4A3E39]">
              <p className="text-sm font-black text-[#A46D4C]">このアカウントでは、有効なプランが確認できませんでした。</p>
              <p className="mt-3 text-xs leading-6 text-[#6B5851]">
                登録済みなのに利用できない場合、別のログイン方法で入っている可能性があります。
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <ReLoginMethodNotice onLogout={handleLogout} />
                <p className="mt-1 text-center text-xs text-[#A67C52]">（問い合わせ先は後日公開予定です）</p>
              </div>
            </div>
          ) : null}

          {vm ? (
            <>
              <section className="relative mb-6 mt-2 flex w-full flex-col items-center">
                <div className="absolute -right-4 -top-8 h-36 w-36 rounded-full bg-[#F4E3D7] opacity-60 blur-3xl" />

                <div className="relative h-64 w-64 overflow-hidden rounded-full border-[6px] border-white bg-white shadow-[0_10px_25px_rgba(166,124,82,0.2)] animate-[bounce_5s_infinite]">
                  <img
                    src={portraitSrc}
                    alt={vm.characterName || "WizMate character"}
                    onError={() => {
                      if (portraitSrc !== "/img/no-image.png") {
                        setPortraitSrc("/img/no-image.png");
                      }
                    }}
                    className="h-full w-full object-cover object-top"
                  />
                </div>

                <div className="relative -mt-4 w-full max-w-[18rem] rounded-[1.8rem] border border-white/90 bg-white px-5 py-4 text-center shadow-[0_16px_30px_rgba(166,124,82,0.15)]">
                  <span className="absolute left-10 top-[-10px] h-5 w-5 rotate-45 border-l border-t border-white/90 bg-white" />
                  <p className="whitespace-pre-line text-[15px] font-bold leading-7 text-[#5D4D46]">
                    {resolveHomeGreeting(messageToUser)}
                  </p>
                </div>
              </section>

              <section className="mb-8 grid w-full grid-cols-2 gap-3">
                <HomeActionCard
                  to="/talk"
                  title={`${characterLastName}と話す`}
                  description="会話・相談・壁打ちへ"
                  locked={isTalkLocked}
                  variant="primary"
                  onLockedClick={openInviteModal}
                />

                <HomeActionCard
                  to="/request"
                  title={`${characterLastName}に頼む`}
                  description="情報収集・整理・通知をおまかせ"
                  locked={isRequestLocked}
                  variant="secondary"
                  onLockedClick={openInviteModal}
                />
              </section>

              <section className="w-full rounded-[2rem] border border-[#EADBC8] bg-white/40 p-6">
                <div className="mb-4 flex items-center gap-2">
                  <span className="text-[13px] font-black uppercase tracking-widest text-[#A67C52]">
                    {characterLastName}からの報告{showYesterday ? "・前日分" : ""}
                  </span>
                  <div className="h-px flex-1 bg-[#EADBC8]" />
                  {hasOlderLogs ? (
                    <button
                      type="button"
                      onClick={() => setShowYesterday((prev) => !prev)}
                      className="shrink-0 text-[11px] font-black text-[#A67C52] underline"
                    >
                      {showYesterday ? "今日を見る" : "前日を見る"}
                    </button>
                  ) : null}
                </div>

                <ul className="space-y-3">
                  {displayedJobLogs.length > 0 ? (
                    <>
                      {visibleJobLogs.map((log, idx) => {
                        const content = (
                          <>
                            <span className="whitespace-pre-line pr-2 text-[15px] font-bold leading-snug text-[#5D4D46]">
                              ・{log.text}
                            </span>
                            <span className="ml-2 mt-1 shrink-0 text-[11px] font-black text-[#A67C52]">
                              {log.time}
                            </span>
                          </>
                        );

                        return (
                          <li
                            key={log.id}
                            className={idx > 0 ? "border-t border-white/50 pt-3" : ""}
                          >
                            {log.to ? (
                              <Link
                                to={log.to}
                                className="flex items-start justify-between rounded-[1rem] transition hover:bg-white/60"
                              >
                                {content}
                              </Link>
                            ) : (
                              <div className="flex items-start justify-between">{content}</div>
                            )}
                          </li>
                        );
                      })}
                      {!expandedJobLogs && hiddenCount > 0 ? (
                        <li>
                          <button
                            type="button"
                            onClick={() => setExpandedJobLogs(true)}
                            className="text-[11px] font-black text-[#A67C52] underline"
                          >
                            もっと見る（{hiddenCount}件）
                          </button>
                        </li>
                      ) : null}
                    </>
                  ) : (
                    <li className="text-[14px] font-medium text-[#7B6861]">まだメモはありません。</li>
                  )}
                </ul>
              </section>
            </>
          ) : null}
        </main>

        <footer className="w-full py-8 text-center opacity-30">
          <AppVersionText className="font-mono text-[10px] uppercase tracking-widest" />
        </footer>
      </div>

      {isInviteModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#4A3E39]/45 px-5 py-8">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="invite-code-dialog-title"
            className="w-full max-w-sm rounded-[1.8rem] border border-[#EADBC8] bg-white px-5 py-5 shadow-[0_22px_60px_rgba(74,62,57,0.22)]"
          >
            <h2 id="invite-code-dialog-title" className="text-base font-black text-[#5D4D46]">
              WizMateを利用するには招待コードが必要です
            </h2>
            <p className="mt-2 text-sm font-medium leading-6 text-[#7B6861]">招待コードを入力してください。</p>
            <div className="mt-4">
              <ReLoginMethodNotice onLogout={handleLogout} />
            </div>
            <form className="mt-5" onSubmit={handleInviteApply}>
              <label htmlFor="home-invite-code" className="sr-only">
                招待コード
              </label>
              <input
                id="home-invite-code"
                value={inviteCode}
                onChange={(event) => {
                  setInviteCode(event.target.value);
                  setInviteApplyError(null);
                }}
                autoComplete="off"
                className="w-full rounded-xl border border-[#D9C4AF] bg-white px-3 py-3 text-sm font-bold text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:ring-2 focus:ring-[#EADBC8]"
                placeholder="招待コードを入力"
              />
              {inviteApplyError ? (
                <p role="alert" className="mt-3 rounded-xl bg-[#FFF3F1] px-3 py-3 text-xs font-bold leading-5 text-[#A1493E]">
                  {inviteApplyError}
                </p>
              ) : null}
              <div className="mt-4 grid grid-cols-2 gap-3">
                <button
                  type="submit"
                  disabled={inviteApplyLoading}
                  className="rounded-xl bg-[#7C5A4E] px-3 py-3 text-xs font-black text-white transition hover:bg-[#6A4D43] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {inviteApplyLoading ? "適用しています..." : "招待コードを適用する"}
                </button>
                <button
                  type="button"
                  disabled={inviteApplyLoading}
                  onClick={closeInviteModal}
                  className="rounded-xl border border-[#D9C4AF] bg-white px-3 py-3 text-xs font-black text-[#7C5A4E] transition hover:bg-[#FDF8F5] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  キャンセル
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {isPrivilegeModalOpen ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#4A3E39]/45 px-5 py-8">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="privilege-code-dialog-title"
            className="w-full max-w-sm rounded-[1.8rem] border border-[#EADBC8] bg-white px-5 py-5 shadow-[0_22px_60px_rgba(74,62,57,0.22)]"
          >
            <h2 id="privilege-code-dialog-title" className="text-base font-black text-[#5D4D46]">
              特典コードを入力
            </h2>
            {privilegeApplySuccess ? (
              <>
                <p className="mt-3 rounded-xl bg-[#F1FFF5] px-3 py-3 text-sm font-bold leading-6 text-[#2D7A4F]">
                  特典コードを適用しました！
                </p>
                <div className="mt-4">
                  <button
                    type="button"
                    onClick={closePrivilegeModal}
                    className="w-full rounded-xl bg-[#7C5A4E] px-3 py-3 text-xs font-black text-white transition hover:bg-[#6A4D43]"
                  >
                    閉じる
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="mt-2 text-sm font-medium leading-6 text-[#7B6861]">
                  特典コードを入力して追加特典を受け取ります。
                </p>
                <form className="mt-5" onSubmit={handlePrivilegeApply}>
                  <label htmlFor="home-privilege-code" className="sr-only">
                    特典コード
                  </label>
                  <input
                    id="home-privilege-code"
                    value={privilegeCode}
                    onChange={(event) => {
                      setPrivilegeCode(event.target.value);
                      setPrivilegeApplyError(null);
                    }}
                    autoComplete="off"
                    className="w-full rounded-xl border border-[#D9C4AF] bg-white px-3 py-3 text-sm font-bold text-[#4A3E39] outline-none transition focus:border-[#A67C52] focus:ring-2 focus:ring-[#EADBC8]"
                    placeholder="特典コードを入力"
                  />
                  {privilegeApplyError ? (
                    <p role="alert" className="mt-3 rounded-xl bg-[#FFF3F1] px-3 py-3 text-xs font-bold leading-5 text-[#A1493E]">
                      {privilegeApplyError}
                    </p>
                  ) : null}
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <button
                      type="submit"
                      disabled={privilegeApplyLoading}
                      className="rounded-xl bg-[#7C5A4E] px-3 py-3 text-xs font-black text-white transition hover:bg-[#6A4D43] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {privilegeApplyLoading ? "適用しています..." : "特典コードを適用する"}
                    </button>
                    <button
                      type="button"
                      disabled={privilegeApplyLoading}
                      onClick={closePrivilegeModal}
                      className="rounded-xl border border-[#D9C4AF] bg-white px-3 py-3 text-xs font-black text-[#7C5A4E] transition hover:bg-[#FDF8F5] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      キャンセル
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      ) : null}

      {isUsageModalOpen && accountState?.authStatus === "authenticated" ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#4A3E39]/45 px-5 py-8">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="usage-summary-dialog-title"
            className="max-h-full w-full max-w-sm overflow-y-auto rounded-[1.8rem] border border-[#EADBC8] bg-white px-5 py-5 shadow-[0_22px_60px_rgba(74,62,57,0.22)]"
          >
            <UsageSummary accountState={accountState} rows={usageRows} titleId="usage-summary-dialog-title" />
            <button
              type="button"
              onClick={closeUsageModal}
              className="mt-4 w-full rounded-xl border border-[#D9C4AF] bg-white px-3 py-3 text-xs font-black text-[#7C5A4E] transition hover:bg-[#FDF8F5]"
            >
              閉じる
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
